import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { keyframes } from "@emotion/react";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import CloseIcon from "@mui/icons-material/Close";

import { CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
import { useAuth } from "@hooks/useAuth";
import { useSeenVersion } from "@hooks/useSeenVersion";

const enter = keyframes`
  from { opacity: 0; transform: translate(-50%, -8px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;

// just below the navbar, over the page, until the user acts on it. Anchored to the navbar, whose height changes
// with the screen (the client selector joins it on small screens); the navbar never scrolls, so it stays put
const bannerSx = {
  position: "absolute",
  top: "calc(100% + 12px)",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1150,
  // as wide as its content, so the text stays on one line; never wider than the screen
  width: "max-content",
  maxWidth: "calc(100vw - 24px)",
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: "10px 8px 10px 16px",
  borderRadius: "10px",
  borderLeft: "5px solid #ffcc00",
  backgroundColor: "#fff",
  boxShadow: "0 6px 24px rgba(0, 41, 81, 0.22)",
  animation: `${enter} .25s ease-out`,
  flexWrap: { xs: "wrap", sm: "nowrap" }
};

const textSx = {
  flex: 1,
  minWidth: 180,
  fontSize: 15,
  fontWeight: 500,
  color: "#212121",
  whiteSpace: { xs: "normal", sm: "nowrap" }
};

const seeSx = {
  backgroundColor: "#002951",
  color: "#fff",
  textTransform: "none",
  fontWeight: 600,
  "&:hover": { backgroundColor: "#0f3a6e" }
};

/**
 * Banner shown once per version line (1.20, 1.21…) to each user: "Nova versão! Veja o que há de novo".
 * Ver novidades opens the version in Documentação › Novidades; either button marks it as seen, and the mark
 * is kept per user in the browser. Hotfixes keep the line, so they never show it.
 */
const NewVersionBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { version, unseen, markSeen } = useSeenVersion(user?.email);

  // the Documentação page shows the Novidades itself
  if (!unseen || location.pathname.startsWith("/docs")) return null;

  const openNews = () => {
    markSeen();
    navigate(`/docs?novidades=${encodeURIComponent(version)}`);
  };

  return (
    <Paper sx={bannerSx} role="status" aria-live="polite" elevation={0}>
      <NewReleasesIcon sx={{ color: "#002951", fontSize: 30, flex: "none" }} />
      <Typography sx={textSx}>{t(CustomerMessages.newVersionBanner).replace("<VERSION>", version)}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto", flex: "none" }}>
        <Button variant="contained" disableElevation sx={seeSx} onClick={openNews}>
          {t(CustomerMessages.newVersionSee)}
        </Button>
        <Button sx={{ textTransform: "none", color: "#002951" }} onClick={markSeen}>
          {t(CustomerMessages.newVersionDismiss)}
        </Button>
        <IconButton size="small" aria-label={t(CustomerMessages.newVersionDismiss)} onClick={markSeen}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default NewVersionBanner;
